import React, { useRef, useState, useEffect } from 'react';
import { useSnapshot } from 'valtio';
import { useNavigate } from 'react-router-dom';
import { X } from 'lucide-react';

import { state } from '../store/global';
import API from '../lib/api';
import findMessage from '../utils/messages';
import {
  getFlag,
  getRouteName,
  getAccountNumber,
  VirtualAccountCurrencies,
  getVACreationText,
} from '../utils/constants';

import Notification from '../components/common/Notification';
import BankAccountModal from '../components/modal/BankAccountModal';
import IconButton from '../components/common/IconButton';
import NormalButton from '../components/common/NormalButton';

import PlusIcon from '../assets/images/plus-white.svg';
import LogoIcon from '../assets/images/logo_no_letter.svg';
import DotThreeVerticalIcon from '../assets/images/dotsThreeVertical.svg';
import SuccessIcon from '../assets/images/submitted.svg';

export default function Account() {
  const snap = useSnapshot(state);
  const navigate = useNavigate();

  const bankItemRefs = [useRef(null), useRef(null), useRef(null)];
  const [notification, setNotification] = useState(null);
  const [isOpenMenuId, setIsOpenMenuId] = useState(-1);
  const [isOpenBankAccount, setIsOpenBankAccount] = useState(false);
  const [selectedBank, setSelectedBank] = useState(null);

  // Virtual account create modal data
  const [isOpenVAModal, setIsOpenVAModal] = useState(false);
  const [currencies, setCurrencies] = useState([]);
  const [virtualAccounts, setVirtualAccounts] = useState([]);
  const [balance, setBalance] = useState(0);
  const [createdVA, setCreatedVA] = useState(false);

  const fetchData = async () => {
    await API.get('user/getVirtualAccounts')
      .then((res) => {
        console.log('getVirtualAccounts: ', res);
        setBalance(res.data.data.balance);
        setVirtualAccounts(res.data.data.virtualAccounts);
      })
      .catch((err) => {
        console.log('err: ', err);
        if (err.status == 401) navigate('/login');
      });
  };

  const enableSpei = async () => {
    API.put('user/enableSpei')
      .then((res) => {
        console.log('enable spei: ', res.data);
      })
      .catch((err) => {
        console.log('err: ', err);
        if (err.status == 401) navigate('/login');
      });
  };
  const fetchVirtualAccountsByBridge = async () => {
    await API.get('user/getAllVirtualAccounts')
      .then((res) => {
        console.log('fetchVirtualAccountsByBridge: ', res);
      })
      .catch((err) => {
        console.log('getAllVirtualAccounts err: ', err);
        if (err.status == 401) navigate('/login');
      });
  };
  const activateVirtualAccount = async () => {
    await API.put('user/activateVirtualAccount', {
      virtualAccountId: '17dac478-b4ce-4599-8f53-5745336e616a',
    })
      .then((res) => {
        console.log('activateVirtualAccount: ', res);
      })
      .catch((err) => {
        console.log('activateVirtualAccount err: ', err);
        if (err.status == 401) navigate('/login');
      });
  };
  const deactivateVirtualAccount = async () => {
    await API.put('user/deactivateVirtualAccount', {
      virtualAccountId: '8523cf7b-b107-4bba-a20f-2d5989c20894',
    })
      .then((res) => {
        console.log('deactivateVirtualAccount: ', true);
      })
      .catch((err) => {
        console.log('deactivateVirtualAccount err: ', err);
        if (err.status == 401) navigate('/login');
      });
  };

  useEffect(() => {
    // showNotification('¡La información se copio exitosamente!', 'success');
    fetchData();
    // enableSpei();
    // activateVirtualAccount();
    // deactivateVirtualAccount();
    // fetchVirtualAccountsByBridge();
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      bankItemRefs.map((item) => {
        if (item.current && !item.current.contains(event.target)) {
          setIsOpenMenuId(-1);
        }
      });
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const openModal = (idx) => {
    setSelectedBank(virtualAccounts[idx]);
    setIsOpenMenuId(-1);
    setIsOpenBankAccount(true);
  };

  const onCreateVirtualAccount = async () => {
    console.log('currencies: ', currencies);
    if (currencies.length == 0) return;
    await API.post('user/createVirtualAccount', { currencies: currencies })
      .then(async (res) => {
        console.log('res: ', res);
        await fetchData();
        setIsOpenVAModal(false);
        setCreatedVA(true);
      })
      .catch((err) => {
        const msg = err?.response?.data?.message || err?.message;
        console.log('err: ', err);
        if (err.status == 401) navigate('/login');

        setNotification({
          message: findMessage(msg),
          type: 'error',
        });
      });
  };
  const onChangeCurrency = (currency) => {
    if (currencies.includes(currency)) {
      setCurrencies(currencies.filter((i) => i !== currency));
    } else setCurrencies([...currencies, currency]);
  };
  const alreadyCreated = (currency) => {
    const selectedVirtualAccounts = virtualAccounts.filter(
      (item) => item.source_deposit_instructions.currency == currency
    );
    if (selectedVirtualAccounts.length) return true;
    return false;
  };

  return (
    <>
      <div className="relative px-10 py-8 space-y-8">
        {notification && (
          <Notification
            message={notification.message}
            type={notification.type}
            onClose={() => setNotification(null)}
          />
        )}

        <div className="sm:flex sm:border-b-1 sm:border-b-[#E9EAEB] items-center justify-between">
          <div className="font-[poppins] py-4 pr-2 space-y-1">
            <p className="text-xl font-semibold text-[#181D27]">Cuentas</p>
            <p className="text-sm font-normal text-[#535862]">
              Consulta tus saldos y administra tus cuentas
            </p>
          </div>
          <span className="space-x-3 flex">
            <IconButton
              disabled={false}
              bgColor="bg-[#092F7B]"
              icon={PlusIcon}
              text="Solicitar cuenta"
              textColor="text-[#FDFDFD]"
              onClick={() => setIsOpenVAModal(true)}
            />
          </span>
        </div>

        <div className="p-6 space-y-2 bg-white rounded-[20px] border-1 border-[#E6EBF7] shadow-xs">
          <div className="p-6 rounded-[20px] bg-[#F9FAFB] border-1 border-[#D5D7DA] flex items-center space-x-2">
            <div className="w-[46px] h-[46px] flex items-center justify-center">
              <img src={LogoIcon} alt="logo" className="w-[38px] h-[35px]" />
            </div>
            <div className="space-y-1 font-[poppins]">
              <p className="text-sm text-[#181D27] font-medium leading-[22px] tracking-[0%]">
                Saldo acumulado
              </p>
              <p className="text-lg text-[#0A0D12] font-semibold leading-[20px] flex items-center space-x-1">
                <span>${balance}</span>
                <span className="text-xs text-[#535862] font-medium leading-[14px]">
                  USD
                </span>
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-2">
            {virtualAccounts.map((item, idx) => {
              return (
                <div
                  key={idx}
                  className="relative flex-1 min-w-56 rounded-[20px] bg-[#FDFDFD] border-1 border-[#E9EAEB]"
                >
                  <div className="px-6 py-4 space-x-2 flex items-center justify-between">
                    <div className="flex items-center justify-center space-x-2">
                      <div className="w-[38px] h-[38px] flex items-center justify-center">
                        <img
                          src={getFlag(item)}
                          alt="logo"
                          className="w-[38px] h-[38px]"
                        />
                      </div>
                      <div className="space-y-1 text-sm font-normal min-w-0 flex-1">
                        <p className="text-[#181D27]">
                          {getRouteName(
                            item.source_deposit_instructions.currency
                          )}
                        </p>
                        <p className="text-[#717680] truncate max-w-[220px]">
                          {getAccountNumber(item)}
                        </p>
                      </div>
                    </div>

                    <button
                      onClick={() => setIsOpenMenuId(idx)}
                      className="relative w-6 h-6 cursor-pointer rounded-[20px] hover:bg-gray-200 flex items-center justify-center"
                    >
                      <img
                        src={DotThreeVerticalIcon}
                        alt="Dot Three Vertical Icon"
                        className="w-6 h-6"
                      />
                    </button>
                  </div>

                  {isOpenMenuId === idx && (
                    <div
                      ref={bankItemRefs[idx]}
                      className="absolute right-10 bottom-[-20px] bg-white border border-gray-200 rounded-tl-[10px] rounded-bl-[10px] rounded-br-[10px] shadow-lg z-50"
                    >
                      <ul className="py-1 text-sm text-gray-700">
                        <li>
                          <button
                            onClick={() => openModal(idx)}
                            className="w-full cursor-pointer text-xs font-normal text-[#414651] px-4 py-2 hover:bg-gray-100"
                          >
                            Ver datos
                          </button>
                        </li>
                      </ul>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {isOpenBankAccount && (
        <BankAccountModal
          data={selectedBank}
          onClose={() => setIsOpenBankAccount(false)}
        />
      )}

      {isOpenVAModal && (
        <div>
          <div className="fixed z-50 inset-0 bg-black opacity-30 w-full h-full"></div>
          <div className="z-51 fixed inset-0 flex items-center justify-center">
            <div className="w-full mx-4 md:mx-0 md:w-[614px] bg-white rounded-xl shadow-xl rounded-[20px]">
              <div className="p-6 pb-4 border-b-1 border-b-[#CDD9F0] flex items-center justify-between">
                <p className="text-lg text-[#181D27] font-medium">
                  Solicitar cuenta
                </p>
                <button
                  onClick={() => {
                    setIsOpenVAModal(false);
                    setCurrencies([]);
                  }}
                  className="cursor-pointer hover:bg-[#E9EAEB]"
                >
                  <X className="w-5 h-5 text-[#181D27] cursor-pointer" />
                </button>
              </div>

              <div className="p-6 space-y-4">
                <p className="text-sm text-[#535862] font-normal">
                  Selecciona las cuentas que desear solicitar.
                </p>

                {VirtualAccountCurrencies.map((item, idx) => (
                  <div
                    key={idx}
                    className="bg-[#F9FAFB] border border-[#E9EAEB] rounded-[10px] px-6 py-2.5 space-x-3.5 flex items-center justify-center"
                  >
                    <input
                      disabled={alreadyCreated(item.currency) ? true : false}
                      type="checkbox"
                      onChange={() => onChangeCurrency(item.currency)}
                      className="form-checkbox h-5 w-5 cursor-pointer"
                    />

                    <img
                      src={item.flag}
                      alt={item.currency}
                      className="w-9.5 h-9.5 rounded-full"
                    />

                    <p className="w-full font-[poppins] text-sm text-[#181D27] font-medium leading-[22px]">
                      {item.text}
                    </p>
                  </div>
                ))}
              </div>

              <div className="px-6 mb-6 flex float-right">
                <NormalButton
                  disabled={currencies && currencies.length ? false : true}
                  customClass={`px-14 ${
                    currencies && currencies.length
                      ? 'bg-[#092F7B] text-[#FDFDFD]'
                      : 'bg-[#E9EAEB] text-[#A4A7AE]'
                  }`}
                  text="Solicitar"
                  onClick={onCreateVirtualAccount}
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {createdVA && (
        <div>
          <div className="fixed z-50 inset-0 bg-black opacity-30 w-full h-full"></div>
          <div className="z-51 fixed inset-0 flex items-center justify-center">
            <div className="w-full mx-4 md:mx-0 md:w-116 p-6 space-y-6 bg-white rounded-xl shadow-xl rounded-[20px]">
              <div className="space-y-2">
                <div className="py-4 space-y-2 flex flex-col items-center justify-center border-b-1 border-b-[#E9EAEB]">
                  <div className="flex items-center justify-center w-10 h-10 rounded-full bg-[#D1FADF]">
                    <img
                      src={SuccessIcon}
                      alt="success icon"
                      className="w-6 h-6 rounded-full"
                    />
                  </div>
                  <p className="font-[poppins] text-xl text-[#333333] font-semibold leading-[20px]">
                    Cuentas solicitadas
                  </p>
                </div>

                <div className="font-[poppins] text-sm text-[#717680] font-normal leading-[22px] space-y-5.5">
                  <p>Recibimos tu solicitud para las cuentas</p>

                  <ul className="list-disc list-inside rounded-md">
                    {currencies.map((item, idx) => (
                      <li key={idx} className="font-semibold">
                        Flowex Pay {getVACreationText(item)}
                      </li>
                    ))}
                  </ul>

                  <p>Se abrirán en el transcurso de la próximas 24 hs. </p>
                </div>
              </div>

              <div className="flex float-right">
                <NormalButton
                  customClass="px-15 py-2 bg-[#092F7B] text-[#FDFDFD]"
                  text="Aceptar"
                  onClick={() => {
                    setCreatedVA(false);
                    setCurrencies([]);
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
